import { Box, Container, Flex, Grid, Heading, Text } from '@chakra-ui/react';
import { ButtonFullWidthMobile } from '@components/Button';
import { StarBackground } from '@components/StarBackground';
import {
  APP_ROUTES_ABSOLUTE,
  AppLink,
  Badge,
  GradientShadowOverlay,
  LANDING_ROUTES,
  pxToRem,
  SHARED_ROUTES,
} from '@cyberkongz/ui';
import { KollectionBreadcrumb } from '@features/kollection/KollectionBreadcrumb';
import {
  CardsSliderSection,
  CardsVerticalItemsType,
  CardsVerticalSection,
  COMMON_USE_CASES,
} from '@features/kollection/KollectionCards';
import { IntroSectionWrapper } from '@features/kollection/KollectionIntroSection';
import { KollectionStats } from '@features/kollection/KollectionStat';
import { PageSEOTags } from '@features/seo/SEO';
import { useAPIEcosystemSummary } from '@hooks/useDataSummary';
import { APIEcosystemSummarySuccessResponse } from '@pages/api/ecosystem-summary';

function KOLLECTION_STATS(data?: APIEcosystemSummarySuccessResponse) {
  return [
    { number: data?.collections.vxMinted, label: 'Minted' },
    { number: data?.collections.vxHolders, label: 'Holders' },
    {
      number: data?.collections.vxUnclaimed,
      label: 'Unclaimed',
      link: { label: 'Claim your vx', href: APP_ROUTES_ABSOLUTE.CLAIM },
    },
  ];
}

function IntroSection() {
  const { data } = useAPIEcosystemSummary();

  return (
    <IntroSectionWrapper
      openseaCollectionLink={SHARED_ROUTES.MAVIS_MARKET_COLLECTION_VX}
      stylesContainer={{
        pt: [pxToRem(250), 40],
        backgroundImage: [
          'url(/static/kollection/vx_bg_mobile.jpg)',
          'url(/static/kollection/vx_bg.jpg)',
        ],
        backgroundRepeat: 'no-repeat',
        backgroundPosition: ['top 50px center', 'center'],
        backgroundSize: ['contain', 'cover'],
      }}
      bgGradient="radial-gradient(43.39% 43.39% at 50% 50%, rgba(3, 41, 49, 0.3) 0%, rgba(1, 124, 143, 0.5) 0.01%, rgba(1, 124, 143, 0) 100%)"
    >
      <Grid gap={4}>
        <KollectionBreadcrumb label="VX" />
        <Heading variant="displayTertiary">VX</Heading>
        <Text
          css={{
            color: 'textSecondary',
            maxWidth: '38.125em',
          }}
        >
          Not content with taking over the 2D NFT space, the continued evolution thrusts CyberKongz
          into the metaverse in the form of 3D voxel-based avatars. Built to be used in online
          metaverse environments such as The Sandbox. The CyberKongz team&apos;s insatiable need to
          continue to take over the metaverse drove their recent land purchases as well as ambitious
          plans to create phenomenal environments for CyberKongz holders to socialise and interact.
        </Text>
        <Flex my={4} gap={3}>
          <Badge withDot>Mint Completed</Badge>
          <Badge badgeColor="primary" withDot>
            Claim Available
          </Badge>
        </Flex>
        <KollectionStats stats={KOLLECTION_STATS(data)} />
      </Grid>
    </IntroSectionWrapper>
  );
}

const CARDS_CAROUSEL_ITEMS = [
  {
    image: 'metaverse_land_3d.png',
    title: 'Metaverse',
    description: 'Your Avatar for ANY Metaverse.',
  },
  {
    image: 'logo_pnk.png',
    title: 'Play & Kollect',
    description: (
      <Box>
        Go on Adventures and Kollect loot in the on-chain game{' '}
        <AppLink href={SHARED_ROUTES.DOCS}>Play & Kollect</AppLink>.
      </Box>
    ),
  },
  {
    image: 'rainbow_shades.png',
    title: '3D Files',
    description: (
      <Box>
        Access to the primary{' '}
        <AppLink href={LANDING_ROUTES.BLOG_POST('updated-fbx-files-in-the-jungle')}>
          3D file formats
        </AppLink>{' '}
        needed for game design and animation.
      </Box>
    ),
  },
  {
    image: 'gaming_integrations.png',
    title: 'Gaming Integrations ',
    description: (
      <Box>
        Integrations with some of the leading{' '}
        <AppLink href={SHARED_ROUTES.PARTNERSHIPS}>Web3 games</AppLink>.
      </Box>
    ),
  },
];

const CARDS_VERTICAL_ITEMS: CardsVerticalItemsType = [
  {
    image: 'sandbox.svg',
    title: 'Sandbox Ready',
    description: (
      <Box>
        Compatible and Integrated ready to explore{' '}
        <AppLink href="https://medium.com/sandbox-game/cyberkongz-vx-partner-with-the-sandbox-d1025257b8e9">
          {' '}
          The Sandbox
        </AppLink>
        !
      </Box>
    ),
  },
  {
    image: 'ens.png',
    title: 'ENS Subdomains',
    description:
      'If you own a VX Kong, you can get a kongz.eth ENS Subdomain (Ethereum Name Service)!',
  },
  COMMON_USE_CASES.customize,
  {
    image: 'guardian_contract.png',
    title: 'Guardian Contract ',
    description: (
      <Box>
        <AppLink href={LANDING_ROUTES.GUARDIAN_CONTRACT_ARTICLE}>On-chain 2FA security</AppLink>
        {` `}
        (only on Polygon)
      </Box>
    ),
  },
  {
    image: 'avatar_vx.png',
    title: '2D Avatars / Sprites',
    description: '2D Sprites are available to download for all VX Kongz for use in 2D Metaverses.',
  },

  COMMON_USE_CASES.vote,
  COMMON_USE_CASES.merch,
];

function JoinAdventureSection() {
  return (
    <Box
      css={{
        position: 'relative',
        pt: [10, pxToRem(128)],
        pb: [20, 40, pxToRem(510)],
        bg: 'url(/static/kollection/vx_adventure_bg.jpg) center / cover no-repeat',
      }}
    >
      <Container position="relative" zIndex="2">
        <Grid gap={5} css={{ maxW: pxToRem(495) }}>
          <Heading size="sm">JOIN THE ADVENTURE RUNS!</Heading>
          <Text mb={5}>
            Play & Kollect is the ultimate gamified NFT minting experience. Holders can send crews
            of CyberKongz VX&apos;s deep into the jungle in search of epic rewards randomized by
            Ronin VRF.
          </Text>
          <Flex gap={4} flexDirection={['column', 'row']}>
            <ButtonFullWidthMobile href={APP_ROUTES_ABSOLUTE.JUNGLE_ADVENTURE}>
              learn more
            </ButtonFullWidthMobile>
            <ButtonFullWidthMobile href={LANDING_ROUTES.KOLLECTIONS} variant="display">
              view kollections
            </ButtonFullWidthMobile>
          </Flex>
        </Grid>
      </Container>
      <GradientShadowOverlay top />
      <GradientShadowOverlay />
    </Box>
  );
}

export default function VXPage() {
  return (
    <Box css={{ width: '100%' }}>
      <PageSEOTags pageKey="kollections.vx" />
      <IntroSection />
      <StarBackground>
        <CardsSliderSection cards={CARDS_CAROUSEL_ITEMS} />
        <CardsVerticalSection cards={CARDS_VERTICAL_ITEMS} />
      </StarBackground>
      <JoinAdventureSection />
    </Box>
  );
}
